/**
 * The New Cartesian sign convention, as a READOUT (engine target §7).
 *
 * The engine stores f > 0 for anything that converges, whichever way light
 * crosses it. The convention is a fact about one crossing — measured from the
 * element, positive in the direction the light was going — so it is worked out
 * here, per element, per crossing, and nowhere else.
 *
 *   lens:    1/v − 1/u = 1/f,  m = v/u,   f > 0 converging
 *   mirror:  1/v + 1/u = 1/f,  m = −v/u,  f < 0 converging (concave)
 */
import { defOf } from './registry';
import { Part, Stage, World } from './types';

export interface Formula {
    applies: boolean;
    why?: string;
    stages: Stage[];
    working: string[];
    /** Where the final image is along the axis, and the overall magnification. */
    imageX?: number;
    atInfinity?: boolean;
    m?: number;
}

/** "+60.0", "−30.0", "∞" — signed, one decimal, a real minus sign. */
export const signed = (x: number, digits = 1): string => {
    if (!Number.isFinite(x)) return x > 0 ? '+∞' : '−∞';
    const s = Math.abs(x).toFixed(digits);
    return Number(s) === 0 ? s : `${x < 0 ? '−' : '+'}${s}`;
};

const why = {
    offAxis: (name: string): string =>
        `${name} is off the principal axis, so the lens and mirror formulae do not describe it. The rays are drawn where they actually go.`,
    notElement: (name: string): string =>
        `${name} bends the light at real surfaces and does not focus it, so there is no image distance to quote. The rays are drawn where they actually go.`,
    twice: (name: string): string =>
        `The light crosses ${name} more than once, which the formulae do not follow step by step. The image is found from the rays.`,
};

/**
 * Chain the formula through the elements `path` names, in order, for an object
 * point at `objectX` on the axis. Applies only when every one of them — and the
 * object — is mounted on the axis, each is a lens or mirror, and each is crossed
 * once (engine target §8).
 */
export function formulaFor(w: World, objectPart: Part, path: string[], objectX: number): Formula {
    const none = (reason: string): Formula => ({ applies: false, why: reason, stages: [], working: [] });
    const nameOf = (p: Part): string => defOf(p).name(p, w);

    if (objectPart.mount !== 'axis') return none(why.offAxis(nameOf(objectPart)));
    if (path.length === 0) return none('No lens or mirror is in the path of its light.');
    if (new Set(path).size !== path.length) {
        const again = path.find((id, i) => path.indexOf(id) !== i) as string;
        return none(why.twice(nameOf(w.parts.find(p => p.id === again) as Part)));
    }

    const parts = path.map(id => w.parts.find(p => p.id === id) as Part);
    for (const p of parts) {
        if (!defOf(p).element) return none(why.notElement(nameOf(p)));
        if (p.mount !== 'axis') return none(why.offAxis(nameOf(p)));
    }

    const stages: Stage[] = [];
    const working: string[] = [];
    let x = objectX;
    let atInfinity = false;
    let dir = Math.sign(parts[0].at.x - objectX) || 1;
    let m = 1;

    for (const p of parts) {
        const el = defOf(p).element!(p, w);
        const folds = el.folds;
        const name = nameOf(p);
        // u in the convention: from the element, positive along the light.
        const u: number = atInfinity ? -Infinity : (x - p.at.x) * dir;
        if (!atInfinity && Math.abs(u) < 1e-12) return none(`The object is at ${name} itself, so it has no image in it.`);
        const U: number = atInfinity ? 0 : 1 / u;
        const fnc = folds ? -el.f : el.f;
        const P = Number.isFinite(fnc) ? 1 / fnc : 0;
        const V: number = folds ? P - U : P + U;
        const v: number = Math.abs(V) < 1e-15 ? Infinity * (dir > 0 ? 1 : -1) : 1 / V;
        const stageM: number = atInfinity ? 0 : Math.abs(V) < 1e-15 ? Infinity : (folds ? -U / V : U / V);

        stages.push({
            partId: p.id, name, kind: folds ? 'mirror' : 'lens',
            u, v, f: fnc, m: stageM, virtualObject: stages.length > 0 && u > 0,
        });
        working.push(workingLine(name, folds, u, fnc, v, stageM));

        m *= stageM;
        atInfinity = !Number.isFinite(v);
        if (!atInfinity) x = p.at.x + v * dir;
        if (folds) dir = -dir;
    }

    return { applies: true, stages, working, imageX: atInfinity ? undefined : x, atInfinity, m };
}

function workingLine(name: string, mirror: boolean, u: number, f: number, v: number, m: number): string {
    const U = Number.isFinite(u) ? `1/(${signed(u)})` : '0';
    const F = Number.isFinite(f) ? `1/(${signed(f)})` : '0';
    const fText = Number.isFinite(f) ? `${signed(f)} cm` : '∞ (a plane mirror)';
    const uText = Number.isFinite(u) ? `${signed(u)} cm` : '−∞ (a parallel beam)';
    const vText = Number.isFinite(v) ? `${signed(v)} cm` : 'at infinity';
    const mText = Number.isFinite(m) ? signed(m, 2) : '∞';
    return mirror
        ? `${name}: u = ${uText}, f = ${fText}; 1/v = 1/f − 1/u = ${F} − ${U} → v = ${vText}; m = −v/u = ${mText}`
        : `${name}: u = ${uText}, f = ${fText}; 1/v = 1/f + 1/u = ${F} + ${U} → v = ${vText}; m = v/u = ${mText}`;
}
